import i18next from 'i18next'
import React from 'react'
import {
  AsyncState,
  IOWidget,
  Fun,
  fromJSX,
  loadedAsyncState,
  nothing,
  Redirect,
  any,
} from 'widgets-for-react'
import { generateBreadcrumbs } from '../../../../utils/breadcrumbs'
import { ComponentTeasersWrapperLayout } from '../../components/componentTeasers/componentTeasersLayout'
import { componentTeasersWidget } from '../../components/componentTeasers/componentTeasersWidget'
import { CollectionDetailPageHeader } from '../../pageHeader/pageHeaderLayout'
import { paginationWidget } from '../../pagination/paginationWidget'
import { paywallWidget } from '../../paywall/paywallWidget'
import { RootLayout } from '../../rootLayout'
import { routeUpdaters } from '../../routes/routesState'
import { UserSubscriptions } from '../../../../subscription/subscriptionState'
import { collectionDeleterWidget } from '../collectionDeleter/collectionDeleterWidget'
import { collectionEditorWidget } from '../collectionEditor/collectionEditorWidget'
import { CollectionsLoaderState } from '../collectionsLoader/collectionsLoaderState'
import {
  CollectionDetailState,
  collectionDetailStateUpdater,
} from './collectionDetailState'

export const componentsPerPage = 24

export const collectionDetailWidget =
  (
    collectionId: string,
    isLoggedIn: boolean,
    userSubscriptionsState: AsyncState<UserSubscriptions>,
    collectionsState: CollectionsLoaderState,
    page: number
  ): IOWidget<
    CollectionDetailState,
    Fun<
      CollectionDetailState,
      CollectionDetailState & { reloadCollections?: boolean }
    >
  > =>
  (collectionDetailState) => {
    if (collectionsState.kind === 'loaded') {
      const collection = collectionsState.value.find(
        (collection) => collection.Id === collectionId
      )
      return collection !== undefined
        ? paywallWidget(
            isLoggedIn,
            userSubscriptionsState,
            undefined,
            false
          )(
            fromJSX(() => (
              <CollectionDetailPageHeader
                title={collection.Name}
                intro={collection.Description}
              />
            )),
            any([
              fromJSX((setState) => (
                <CollectionDetailPageHeader
                  title={collection.Name}
                  intro={collection.Description}
                  onEdit={() =>
                    setState(
                      collectionDetailStateUpdater.startEditingUpdater(
                        collection.Name,
                        collection.Description
                      )
                    )
                  }
                  breadcrumbs={generateBreadcrumbs(1)}
                  onDelete={() =>
                    setState(
                      collectionDetailStateUpdater.startDeletingCollectionUpdater
                    )
                  }
                />
              )),
              any<
                Fun<
                  CollectionDetailState,
                  CollectionDetailState & { reloadCollections?: boolean }
                >
              >([
                componentTeasersWidget(
                  collectionsState,
                  'collection-detail',
                  'link-delete',
                  {
                    title: i18next.t('component:componentsTitle_plural'),
                  },
                  false,
                  false
                )(
                  loadedAsyncState(
                    collection.Components.slice(
                      componentsPerPage * (page - 1),
                      componentsPerPage * page
                    )
                  )
                ).map(
                  collectionDetailStateUpdater.startDeletingComponentUpdater(
                    collection.Components
                  )
                ),
                paginationWidget(
                  page - 1,
                  componentsPerPage,
                  (newPage: number) =>
                    i18next.language == 'en'
                      ? `/en/dashboard/collection/${collectionId}?page=${
                          newPage + 1
                        }`
                      : `/nl/dashboard/collectie/${collectionId}?page=${
                          newPage + 1
                        }`
                )(loadedAsyncState(collection.Components.length)).never(),
              ]).wrapHTML((element) => (
                <ComponentTeasersWrapperLayout
                  kind="collection-detail"
                  title={i18next.t('component:componentsTitle_plural')}
                  children={element}
                />
              )),
              collectionDeleterWidget(collectionId)(
                collectionDetailState.collectionDeleterState
              ).map(collectionDetailStateUpdater.collectionDeleterUpdater),
              collectionEditorWidget(
                false,
                collectionId,
                collection.Name,
                collection.Description
              )(collectionDetailState.collectionEditorState).map(
                collectionDetailStateUpdater.collectionEditorUpdater
              ),
              collectionDetailState.componentDeleterState.componentId
                ? collectionDeleterWidget(
                    collectionId,
                    collectionDetailState.componentDeleterState.componentId
                  )(collectionDetailState.componentDeleterState).map(
                    collectionDetailStateUpdater.componentDeleterUpdater
                  )
                : nothing(),
            ])
          )
        : fromJSX(() => <RootLayout.Error />)
    } else
      return fromJSX(() =>
        collectionDetailState.collectionDeleterState.componentId ===
          undefined &&
        collectionDetailState.collectionDeleterState.deletingState.kind ===
          'loaded' ? (
          <Redirect to={routeUpdaters(i18next.language).dashboard.url} />
        ) : collectionsState.kind === 'loading' ? (
          <RootLayout.Loader hasWrapper={true} showText={true} />
        ) : (
          <RootLayout.LoadingFailed
            text={i18next.t('common:loadingFailed', {
              entity: i18next.t('collection:collectionsTitle').toLowerCase(),
            })}
          />
        )
      )
  }
